export const TRADE_LIST_LOADING_ACTION_RESET = 'TRADE_LIST_LOADING_ACTION_RESET';
export const TRADE_LIST_LOADING_ACTION_INITIATE = 'TRADE_LIST_LOADING_ACTION_INITIATE';
export const TRADE_LIST_LOADING_ACTION_METERS_LOADED = 'TRADE_LIST_LOADING_ACTION_METERS_LOADED';
export const TRADE_LIST_LOADING_ACTION_RULES_LOADED = 'TRADE_LIST_LOADING_ACTION_RULES_LOADED';
export const TRADE_LIST_LOADING_ACTION_TRADES_LOADED = 'TRADE_LIST_LOADING_ACTION_TRADES_LOADED';
export const TRADE_LIST_LOADING_ACTION_DATA_PREPARED = 'TRADE_LIST_LOADING_ACTION_DATA_PREPARED';
export const TRADE_LIST_LOADING_ACTION_DATA_RENDERED = 'TRADE_LIST_LOADING_ACTION_DATA_RENDERED';
export const TRADE_LIST_LOADING_ACTION_HIDE_OVERLAY = 'TRADE_LIST_LOADING_ACTION_HIDE_OVERLAY';
export const TRADE_LIST_LOADING_ACTION_RAISE_ERROR = 'TRADE_LIST_LOADING_ACTION_RAISE_ERROR';
export const TRADE_LIST_LOADING_ACTION_CANCEL_REQUEST = 'TRADE_LIST_LOADING_ACTION_CANCEL_REQUEST';
export const TRADE_LIST_LOADING_ACTION_LOADING_COMPLETE = 'TRADE_LIST_LOADING_ACTION_LOADING_COMPLETE';

export const TradeListLoadingActions = Object.freeze({
  RESET: TRADE_LIST_LOADING_ACTION_RESET,
  INITIATE: TRADE_LIST_LOADING_ACTION_INITIATE,
  METERS_LOADED: TRADE_LIST_LOADING_ACTION_METERS_LOADED,
  RULES_LOADED: TRADE_LIST_LOADING_ACTION_RULES_LOADED,
  TRADES_LOADED: TRADE_LIST_LOADING_ACTION_TRADES_LOADED,
  DATA_PREPARED: TRADE_LIST_LOADING_ACTION_DATA_PREPARED,
  DATA_RENDERED: TRADE_LIST_LOADING_ACTION_DATA_RENDERED,
  HIDE_OVERLAY: TRADE_LIST_LOADING_ACTION_HIDE_OVERLAY,
  RAISE_ERROR: TRADE_LIST_LOADING_ACTION_RAISE_ERROR,
  CANCEL_REQUEST: TRADE_LIST_LOADING_ACTION_CANCEL_REQUEST,
  LOADING_COMPLETE: TRADE_LIST_LOADING_ACTION_LOADING_COMPLETE,
});

const TRADE_LIST_LOADING_STATE_IDLE = 'TRADE_LIST_LOADING_STATE_IDLE';
const TRADE_LIST_LOADING_STATE_INITIALISING = 'TRADE_LIST_LOADING_STATE_INITIALISING';
const TRADE_LIST_LOADING_STATE_LOADING_METERS = 'TRADE_LIST_LOADING_STATE_LOADING_METERS';
const TRADE_LIST_LOADING_STATE_LOADING_RULES = 'TRADE_LIST_LOADING_STATE_LOADING_RULES';
const TRADE_LIST_LOADING_STATE_LOADING_TRADES = 'TRADE_LIST_LOADING_STATE_LOADING_TRADES';
const TRADE_LIST_LOADING_STATE_PREPARING_DATA = 'TRADE_LIST_LOADING_STATE_PREPARING_DATA';
const TRADE_LIST_LOADING_STATE_RENDERING_DATA = 'TRADE_LIST_LOADING_STATE_RENDERING_DATA';
const TRADE_LIST_LOADING_STATE_COMPLETE = 'TRADE_LIST_LOADING_STATE_COMPLETE';
const TRADE_LIST_LOADING_STATE_ERROR = 'TRADE_LIST_LOADING_STATE_ERROR';
const TRADE_LIST_LOADING_STATE_CANCELLED = 'TRADE_LIST_LOADING_STATE_CANCELLED';

/**
 * Enumerated loading states for the TradeList data pipeline.
 *
 * The pipeline progresses through the following conceptual stages:
 * - idle:            No request has been made yet
 * - initialising:    A new three-stage fetch has been initiated
 * - loading meters:  Meters are loading
 * - loading rules:   Trade rules are being fetched
 * - loading trades:  Trades are being fetched
 * - preparing data:  All raw data has been loaded and is being prepared
 * - rendering data:  Data is prepared and is now being rendered
 * - complete:        Loading is complete
 * - error:           An error occurred during any stage of the pipeline
 */
export const TradeListLoadingStates = Object.freeze({
  IDLE: TRADE_LIST_LOADING_STATE_IDLE,
  INITIALISING: TRADE_LIST_LOADING_STATE_INITIALISING,
  LOADING_METERS: TRADE_LIST_LOADING_STATE_LOADING_METERS,
  LOADING_RULES: TRADE_LIST_LOADING_STATE_LOADING_RULES,
  LOADING_TRADES: TRADE_LIST_LOADING_STATE_LOADING_TRADES,
  PREPARING_DATA: TRADE_LIST_LOADING_STATE_PREPARING_DATA,
  RENDERING_DATA: TRADE_LIST_LOADING_STATE_RENDERING_DATA,
  COMPLETE: TRADE_LIST_LOADING_STATE_COMPLETE,
  ERROR: TRADE_LIST_LOADING_STATE_ERROR,
  CANCELLED: TRADE_LIST_LOADING_STATE_CANCELLED,
});

/**
 * Message keys used by the loading overlay.
 * Mirrors PropertyShow's LoadingMessageKeys for cross-component consistency.
 */
const LOADING_MESSAGE_NONE = 'none';
const LOADING_MESSAGE_METERS_LOADED = 'meters_loaded';
const LOADING_MESSAGE_RULES_LOADED = 'rules_loaded';
const LOADING_MESSAGE_TRADES_LOADED = 'trades_loaded';
const LOADING_MESSAGE_PREPARING_DATA = 'preparing_data';
const LOADING_MESSAGE_RENDERING = 'rendering';
const LOADING_MESSAGE_COMPLETE = 'complete';

export const LoadingMessageKeys = Object.freeze({
  NONE: LOADING_MESSAGE_NONE,
  METERS_LOADED: LOADING_MESSAGE_METERS_LOADED,
  RULES_LOADED: LOADING_MESSAGE_RULES_LOADED,
  TRADES_LOADED: LOADING_MESSAGE_TRADES_LOADED,
  PREPARING_DATA: LOADING_MESSAGE_PREPARING_DATA,
  RENDERING: LOADING_MESSAGE_RENDERING,
  COMPLETE: LOADING_MESSAGE_COMPLETE,
});

/**
 * Stage keys used to identify individual loading stages for parallel tracking.
 * These are used in the activeStages and completedStages arrays.
 * Mirrors PropertyShow's StageKeys pattern.
 */
const STAGE_KEY_IDLE = 'IDLE';
const STAGE_KEY_PROPERTY = 'PROPERTY';
const STAGE_KEY_METERS = 'METERS';
const STAGE_KEY_RULES = 'RULES';
const STAGE_KEY_TRADES = 'TRADES';
const STAGE_KEY_DATA_PREPARED = 'DATA_PREPARED';
const STAGE_KEY_DATA_RENDERED = 'DATA_RENDERED';
const STAGE_KEY_ERROR = 'ERROR';

export const StageKeys = Object.freeze({
  IDLE: STAGE_KEY_IDLE,
  PROPERTY: STAGE_KEY_PROPERTY,
  METERS: STAGE_KEY_METERS,
  RULES: STAGE_KEY_RULES,
  TRADES: STAGE_KEY_TRADES,
  DATA_PREPARED: STAGE_KEY_DATA_PREPARED,
  DATA_RENDERED: STAGE_KEY_DATA_RENDERED,
  ERROR: STAGE_KEY_ERROR,
});

/**
 * Progress weight for each stage key.
 * Weights represent each stage's share of total work and sum to 100.
 */
export const STAGE_PROGRESS = {
  [StageKeys.IDLE]: 0,
  // Property is already present when TradeList loads, but we keep the stage for UI consistency.
  [StageKeys.PROPERTY]: 5,
  [StageKeys.METERS]: 15,
  [StageKeys.RULES]: 25,
  [StageKeys.TRADES]: 35,
  [StageKeys.DATA_PREPARED]: 10,
  [StageKeys.DATA_RENDERED]: 10,
  [StageKeys.ERROR]: 0,
};
